import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { useAuth } from "@ssf/auth-components";
import { IssuesIcon } from "@ssf/memdb-components";
import { Check, OpenInNew } from "mdi-material-ui";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import type { useAutoAsk } from "../lib/index.js";
import {
  removeAuthAsked,
  useCreateAutoAsked,
  useCreateSignClub,
} from "../lib/index.js";

export const AutoAskClubDialog = ({
  clubs,
}: {
  clubs: NonNullable<ReturnType<typeof useAutoAsk>["data"]>["clubs"];
}) => {
  const { t } = useTranslation("AutoAskClubDialog");
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [open, setOpen] = useState(true);
  const currentYear = new Date().getFullYear();
  const clubsStartedBeforeCurrentYear = clubs.filter(
    ({ startdate }) =>
      Number.parseInt(startdate.substring(0, 4), 10) < currentYear,
  );
  const clubsWithDocumentIssues = clubsStartedBeforeCurrentYear.filter(
    ({ issues }) => issues.some((issue) => !issue.approved),
  );

  const { mutate: createAutoAsked } = useCreateAutoAsked({
    onSuccess() {
      enqueueSnackbar(t("success"), { variant: "success" });
    },
  });
  const { mutate: createSignClub } = useCreateSignClub({
    onSuccess() {
      enqueueSnackbar(t("success"), { variant: "success" });
    },
  });

  return (
    <Dialog
      open={
        open && clubsStartedBeforeCurrentYear.some(({ existing }) => !existing)
      }
    >
      <DialogContent dividers sx={{ "> .MuiTypography-root": { mb: 1 } }}>
        <Typography variant="h3">{t("title", user?.profile || {})}</Typography>

        {clubsWithDocumentIssues.length > 0 && (
          <>
            <Trans
              t={t}
              i18nKey="club.content"
              tOptions={{ postProcess: "markdown" }}
            />

            <Box sx={{ mx: -3 }}>
              <List>
                {clubsWithDocumentIssues.map((club) => (
                  <ListItem key={club.id} disablePadding>
                    <ListItemButton
                      component="a"
                      href={`${process.env.CLUB_WEB_URL}/klubb/${club.id}`}
                      onClick={() => {
                        removeAuthAsked(Number(user?.profile.sub));
                      }}
                      sx={{ py: 0.5, px: 3 }}
                    >
                      <IssuesIcon
                        issues={club.issues}
                        club={club}
                        sx={{ mr: 1 }}
                      />
                      <ListItemText primary={club.name} />
                      {club.issues.some((issue) => !issue.approved) ? (
                        <OpenInNew color="action" fontSize="small" />
                      ) : (
                        <Check color="success" fontSize="small" />
                      )}
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>

              <Alert severity="info" sx={{ px: 3 }}>
                {t("club.info")}
              </Alert>
            </Box>

            <Divider sx={{ mx: -3, my: 2 }} />
          </>
        )}

        <Trans
          t={t}
          i18nKey="membership.content"
          tOptions={{ postProcess: "markdown" }}
        />

        <Typography>{t("membership.hint")}</Typography>
        {clubsStartedBeforeCurrentYear.map((club) => (
          <FormControlLabel
            key={club.id}
            control={
              <Checkbox
                defaultChecked={club.existing}
                disabled={club.existing}
                onChange={({ currentTarget }) => {
                  if (currentTarget.checked && user) {
                    createSignClub({
                      clubId: club.id,
                      createdBy: Number(user.profile.sub),
                    });
                    currentTarget.disabled = true;
                  }
                }}
              />
            }
            sx={{ display: "flex", my: -0.5 }}
            label={club.name}
          />
        ))}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", ml: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              onChange={({ currentTarget }) => {
                if (currentTarget.checked && user) {
                  createAutoAsked({
                    by: "user",
                    memberId: Number(user.profile.sub),
                    type: "club",
                  });
                  currentTarget.disabled = true;
                }
              }}
            />
          }
          label={t("dontAskAgain")}
        />
        <Button onClick={() => setOpen(false)}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
};
